// Web replacement of Offline/TileStore.swift (TrailBivvyKit) and of the dataset files of OfflineStore.swift: Cache
// Storage wrappers shared by every offline region.
//
// - `TileCache` ('tb-tiles-v1'): downloaded tiles keyed by their tile URL; the service worker serves them cache-first.
//   Tiles are idempotent and shared across regions (a tile stored by one region is skipped by the next). Each entry
//   carries `x-tb-bytes` (body size) and the tile's content type.
// - `PinnedDataCache` ('tb-data-pinned-v1'): the route data of a download — the trail pack JSON and the world rules
//   layer — copied out of the evictable caches so they survive cache eviction and service-worker updates.
//
// The service worker (src/sw/sw.js) uses the same cache names; it never deletes these caches.

export const CacheNames = {
  /** Tiles of offline packages (written by OfflineDownloadManager only). */
  tiles: 'tb-tiles-v1',
  /** Tiles seen while browsing (service worker, LRU-capped). */
  ambientTiles: 'tb-tiles-ambient-v1',
  /** Route data pinned by offline packages. */
  pinnedData: 'tb-data-pinned-v1',
  /** Recently opened trail packs (service worker, capped). */
  packs: 'tb-packs-v1',
  /** Prefix of the versioned app-shell caches. */
  shellPrefix: 'tb-shell-',
} as const;

/** Header carrying the stored body size in bytes. */
export const storedBytesHeader = 'x-tb-bytes';

/** Absolute, normalised form of a URL (Cache Storage keys are normalised request URLs). */
export function normalizedURL(url: string, base?: string): string {
  return new URL(url, base).href;
}

/** Downloaded map tiles (Swift `TileStore`), keyed by tile URL. */
export class TileCache {
  private cache?: Promise<Cache>;

  constructor(
    private readonly storage: CacheStorage,
    readonly name: string = CacheNames.tiles,
  ) {}

  private open(): Promise<Cache> {
    this.cache ??= this.storage.open(this.name);
    return this.cache;
  }

  /** Normalised URLs of every stored tile (one Cache Storage call). */
  async urls(): Promise<Set<string>> {
    const keys = await (await this.open()).keys();
    return new Set(keys.map((request) => normalizedURL(request.url)));
  }

  async contains(url: string): Promise<boolean> {
    return (await (await this.open()).match(url)) !== undefined;
  }

  /** Stores (or replaces) a tile. Rejects when the quota is exceeded (QuotaExceededError). */
  async store(url: string, data: Uint8Array, contentType: string): Promise<void> {
    const body = new Blob([data as BlobPart], { type: contentType });
    const response = new Response(body, {
      status: 200,
      headers: { 'content-type': contentType, [storedBytesHeader]: String(data.length) },
    });
    await (await this.open()).put(url, response);
  }

  /** Stored size of a tile (from `x-tb-bytes`), or undefined when missing. */
  async size(url: string): Promise<number | undefined> {
    const response = await (await this.open()).match(url);
    if (!response) return undefined;
    const value = Number(response.headers.get(storedBytesHeader) ?? response.headers.get('content-length'));
    return Number.isFinite(value) ? value : undefined;
  }

  /** Removes tiles; missing ones are ignored. */
  async remove(urls: Iterable<string>): Promise<void> {
    const cache = await this.open();
    for (const url of urls) await cache.delete(url);
  }

  /** Deletes every stored tile. */
  async removeAll(): Promise<void> {
    await this.storage.delete(this.name);
    this.cache = undefined;
  }
}

/** Result of pinning one data file. */
export type PinResult =
  /** The file is in the pinned cache. */
  | 'pinned'
  /** Not cached anywhere and the network failed: retry when online. */
  | 'offline'
  /** The server answered with an error or something that is not JSON. */
  | 'unavailable';

/** Result of refreshing one pinned file from the network. */
export type RefreshResult = { status: 'updated'; json: unknown } | { status: 'offline' } | { status: 'failed' };

/** Same-origin JSON files kept for offline regions (see the module comment). */
export class PinnedDataCache {
  private cache?: Promise<Cache>;

  constructor(
    private readonly storage: CacheStorage,
    private readonly fetchFn: typeof fetch,
    readonly name: string = CacheNames.pinnedData,
  ) {}

  private open(): Promise<Cache> {
    this.cache ??= this.storage.open(this.name);
    return this.cache;
  }

  async isPinned(url: string): Promise<boolean> {
    return (await (await this.open()).match(url)) !== undefined;
  }

  /**
   * Pins `url`: already pinned → nothing to do; a copy in any other cache (the service worker's pack or shell caches)
   * is copied; otherwise the file is fetched (through the service worker).
   */
  async pin(url: string): Promise<PinResult> {
    const cache = await this.open();
    if (await cache.match(url)) return 'pinned';
    const existing = await this.storage.match(url);
    if (existing && existing.ok) {
      await cache.put(url, existing.clone());
      return 'pinned';
    }
    let response: Response;
    try {
      response = await this.fetchFn(url, { credentials: 'same-origin' });
    } catch {
      return 'offline';
    }
    if (!response.ok) return 'unavailable';
    const text = await response.text();
    if (!isJSONDocument(text)) return 'unavailable';
    await cache.put(url, jsonResponse(text, response.headers.get('content-type')));
    return 'pinned';
  }

  /** Removes pinned files (the service worker may still hold evictable copies). */
  async unpin(urls: Iterable<string>): Promise<void> {
    const cache = await this.open();
    for (const url of urls) await cache.delete(url);
  }

  /**
   * Re-downloads a pinned file bypassing every cache (`cache: 'no-cache'`, which the service worker answers from the
   * network). A failed request, an error status or a body that is not a non-empty JSON document keeps the pinned copy.
   */
  async refresh(url: string): Promise<RefreshResult> {
    let response: Response;
    try {
      response = await this.fetchFn(url, { cache: 'no-cache', credentials: 'same-origin' });
    } catch {
      return { status: 'offline' };
    }
    if (!response.ok) return { status: 'failed' };
    const text = await response.text();
    if (!isJSONDocument(text)) return { status: 'failed' };
    await (await this.open()).put(url, jsonResponse(text, response.headers.get('content-type')));
    return { status: 'updated', json: JSON.parse(text) };
  }
}

function jsonResponse(text: string, contentType: string | null): Response {
  return new Response(text, { status: 200, headers: { 'content-type': contentType ?? 'application/json' } });
}

/** A non-empty JSON object or array (an empty answer is treated as a glitch, never as "no data"). */
function isJSONDocument(text: string): boolean {
  try {
    const value: unknown = JSON.parse(text);
    if (Array.isArray(value)) return value.length > 0;
    return typeof value === 'object' && value !== null && Object.keys(value).length > 0;
  } catch {
    return false;
  }
}
